import { Checkbox, Select, Text, Tooltip } from "@gravity-ui/uikit";
import { useAtom } from "@reatom/npm-react";
import { TransformerInfoDto, TransformersEnum } from "definitions/exporter";
import { useMemo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { useTranslate } from "src/i18n";
import { getExportOptionsRequest } from "src/pages/ExportPage/store/fetchers";
import { pluralNumber } from "src/util";

import { Section, SectionBadge } from "../../components";
import {
  TRANSFORMER_GROUPS,
  TransformerGroup,
  isTranslationTransformer,
  languages,
} from "../../ExportTasksForm.constants";
import { ExportTaskFormValues } from "../../ExportTasksForm.types";
import { WatermarkOptionsForm } from "../WatermarkOptionsForm";
import css from "./TransformersForm.module.scss";

const OPTION_LABELS: [string, string, string] = ["опция", "опции", "опций"];

// Стабильный empty-fallback: иначе `watch("transformers") ?? []` отдавал бы
// новый array reference каждый рендер и разрывал useMemo ниже по deps.
const EMPTY_TRANSFORMERS: TransformersEnum[] = [];

export const TransformersForm = () => {
  const { t } = useTranslate();
  const [exportOptions] = useAtom(getExportOptionsRequest.dataAtom);

  const { setValue, watch, control } = useFormContext<ExportTaskFormValues>();

  const selectProductSampleId = watch("selectProductSampleId");
  // Страхуем кейс: бэк вернул задачу без поля transformers — без guard'а
  // .some/.filter/.includes ниже упадут TypeError'ом на первом же рендере.
  const selectedTransformers = watch("transformers") ?? EMPTY_TRANSFORMERS;
  const selectedLanguage = watch("language");
  const watermarkImageUrl = watch("imageTransformOptions.watermarkImageUrl");

  const transformersByKey = useMemo(() => {
    const map = new Map<TransformersEnum, TransformerInfoDto>();
    for (const t of exportOptions.transformers) {
      map.set(t.transformer, t);
    }
    return map;
  }, [exportOptions.transformers]);

  const isTransformerDisabled = !selectProductSampleId;

  const hasActiveTranslation = useMemo(
    () => selectedTransformers.some(isTranslationTransformer),
    [selectedTransformers],
  );

  const visibleGroups = useMemo(
    () =>
      TRANSFORMER_GROUPS.map((group) => ({
        ...group,
        availableTransformers: group.transformers.filter((t) =>
          transformersByKey.has(t),
        ),
      })).filter((g) => g.availableTransformers.length > 0),
    [transformersByKey],
  );

  const handleCheckboxChange = (
    transformer: TransformersEnum,
    isChecked: boolean,
  ) => {
    const updatedTransformers: TransformersEnum[] = isChecked
      ? [...selectedTransformers, transformer]
      : selectedTransformers.filter((item) => item !== transformer);
    setValue("transformers", updatedTransformers);
  };

  const getBadge = (group: TransformerGroup): SectionBadge | undefined => {
    const activeCount = selectedTransformers.filter((t) =>
      group.transformers.includes(t),
    ).length;

    if (group.id === "translation") {
      if (activeCount === 0) return undefined;
      const languageLabel = languages.find(
        (l) => l.value === selectedLanguage,
      )?.label;
      const countText = `${activeCount} ${pluralNumber(activeCount, OPTION_LABELS)}`;
      return {
        text: languageLabel ? `${countText} · ${languageLabel}` : countText,
        theme: "info",
      };
    }

    if (activeCount === 0) return undefined;

    // IMAGE_TRANSFORM без ссылки на лого — фактически no-op на бэке. Сигналим
    // пользователю в свёрнутой секции, чтобы не уехала задача "впустую".
    if (
      group.id === "images" &&
      selectedTransformers.includes(TransformersEnum.ImageTransform) &&
      !watermarkImageUrl
    ) {
      return { text: "Нет лого", theme: "warning" };
    }

    return {
      text: `${activeCount} ${pluralNumber(activeCount, OPTION_LABELS)}`,
      theme: "info",
    };
  };

  return (
    <>
      {visibleGroups.map((group) => {
        const isTranslation = group.id === "translation";
        const isImages = group.id === "images";
        const hasImageTransform =
          isImages &&
          selectedTransformers.includes(TransformersEnum.ImageTransform);
        const hasActiveInGroup = group.transformers.some((t) =>
          selectedTransformers.includes(t),
        );

        return (
          <Section
            key={group.id}
            title={group.title}
            badge={getBadge(group)}
            defaultExpanded={hasActiveInGroup}
          >
            <div className={css.transformersGrid}>
              {group.availableTransformers.map((transformerKey) => {
                const info = transformersByKey.get(transformerKey);
                if (!info) return null;
                return (
                  <Tooltip
                    content={info.description}
                    key={transformerKey}
                    placement="top"
                  >
                    <Checkbox
                      disabled={isTransformerDisabled}
                      checked={selectedTransformers.includes(info.transformer)}
                      onUpdate={(isChecked) =>
                        handleCheckboxChange(info.transformer, isChecked)
                      }
                    >
                      <div className={css.transformerCheckbox}>
                        <div>{info.name}</div>
                      </div>
                    </Checkbox>
                  </Tooltip>
                );
              })}
            </div>

            {group.id === "normalization" && (
              <div className={css.movedHint}>
                <Text variant="caption-2" color="secondary">
                  {t(
                    "export.transformers.roundingMoved",
                    "Округление цены переехало в раздел «Ценовые правила».",
                  )}
                </Text>
              </div>
            )}

            {isTranslation && hasActiveTranslation && (
              <div className={css.languageRow}>
                <Controller
                  name="language"
                  control={control}
                  render={({ field }) => (
                    <Select
                      label="Язык перевода"
                      placeholder="Язык перевода"
                      {...field}
                      value={field.value ? [field.value] : []}
                      onUpdate={(value) => field.onChange(value[0])}
                      popupClassName={css.languageSelectorPopup}
                      width="max"
                      filterable
                    >
                      {languages.map((language) => (
                        <Select.Option
                          key={language.value}
                          value={language.value}
                        >
                          {language.label}
                        </Select.Option>
                      ))}
                    </Select>
                  )}
                />
              </div>
            )}

            {hasImageTransform && <WatermarkOptionsForm />}
          </Section>
        );
      })}
    </>
  );
};
